import { z } from "zod";
import {
  ClassificationType,
  ExtractionType,
  Language,
  ParsingType,
  PredefinedExtraction,
  RegexMatch,
  ValidationSeverity,
} from "../interfaces/doc";

// Classes schema
export const ClasseSchema = z.object({
  name: z.string(),
  description: z.string().optional().nullable(),
});

// SegmentationConfig schema
export const SegmentationConfigSchema = z.object({
  classes: ClasseSchema.array()
    .min(2)
    .refine(
      (classes) => {
        return classes
          ? classes.length ===
              new Set(classes.map((classe) => classe.name))?.size
          : true;
      },
      { message: "Classification classe names are not unique" }
    )
    .refine(
      (classes) => {
        return classes
          ? classes.filter((classe) => !!classe.description).length ===
              new Set(
                classes
                  .filter((classe) => !!classe.description)
                  .map((classe) => classe.name)
              )?.size
          : true;
      },
      { message: "Classification classe description are not unique" }
    ),
  default: ClasseSchema.optional().nullable(),
});

// GroupConfig schema
const GroupConfigSchema = z.object({
  ghost_types: z.string().array().optional(),
  weak_types: z.string().array().optional(),
});

// SplitConfig schema
export const SplitConfigSchema = z
  .object({
    segmentation: SegmentationConfigSchema,
    group: GroupConfigSchema,
  })
  .refine(
    (config) => {
      let classNames = new Set(
        config.segmentation.classes.map(({ name, description }) => name)
      );
      config.segmentation.default &&
        classNames.add(config.segmentation.default.name);
      return (
        config.group.ghost_types?.every((v) => classNames.has(v)) &&
        config.group.weak_types?.every((v) => classNames.has(v))
      );
    },
    { message: "Weak and ghost types must be valid segmentation class names" }
  )
  .refine(
    (config) => {
      const ghost = new Set(config.group.ghost_types);
      const weak = new Set(config.group.weak_types);
      return new Set([...ghost].filter((x) => weak.has(x))).size === 0;
    },
    { message: "Weak and ghost types must be disjoint" }
  );

// Query schema
const QuerySchema = z.object({
  name: z.string().optional(),
  id: z.string().optional(),
  type: z.union([
    z.nativeEnum(ClassificationType),
    z.nativeEnum(ExtractionType),
  ]),
});

// ClassificationConfig schema
const ClassificationQuerySchema = QuerySchema.extend({
  name: z.string().refine((name) => name !== "split", {
    message: "Classification name can't be split",
  }),
  type: z.nativeEnum(ClassificationType),
  classes: ClasseSchema.array()
    .min(2)
    .refine(
      (classes) => {
        return classes
          ? classes.length ===
              new Set(classes.map((classe) => classe.name))?.size
          : true;
      },
      { message: "Classification classe names are not unique" }
    )
    .refine(
      (classes) => {
        return classes
          ? classes.filter((classe) => !!classe.description).length ===
              new Set(
                classes
                  .filter((classe) => !!classe.description)
                  .map((classe) => classe.name)
              )?.size
          : true;
      },
      { message: "Classification classe description are not unique" }
    ),
  default: ClasseSchema.optional().nullable(),
});

// Classification Routed Queries schema
const ClassificationRoutedQueriesSchema = z.object({
  queries: ClassificationQuerySchema.array().min(1),
  routes: z.string().array().min(1),
});

// Classification Union Query
const ClassificationQueriesSchema = z.union([
  ClassificationQuerySchema,
  ClassificationRoutedQueriesSchema,
]);

const ClassificationConfigSchema = z.object({
  queries: ClassificationQueriesSchema.array()
    .min(1)
    .superRefine((queries, ctx) => {
      const queryList: string[] = [];
      const queryDict = new Map<string, string[]>();

      // Process all queries
      queries.forEach((query) => {
        if (!("queries" in query)) {
          queryList.push(query.name);
        } else {
          const routeKey = String(query.routes);
          if (!queryDict.has(routeKey)) {
            queryDict.set(routeKey, []);
          }
          query.queries.forEach((q) => {
            queryDict.get(routeKey)?.push(q.name);
          });
        }
      });

      // Check query name uniqueness in the main list
      const uniqueQueryNames = new Set(queryList);
      if (uniqueQueryNames.size !== queryList.length) {
        const duplicates = new Set(
          queryList.filter(
            (name) => queryList.filter((n) => n === name).length > 1
          )
        );
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Query names are not unique: ${Array.from(duplicates)}`,
          path: ["queries"],
        });
      }

      // Check query name uniqueness for each route
      queryDict.forEach((queryNames, routes) => {
        queryNames.push(...queryList);
        const uniqueRouteQueryNames = new Set(queryNames);
        if (uniqueRouteQueryNames.size !== queryNames.length) {
          const duplicates = new Set(
            queryNames.filter(
              (name) => queryNames.filter((n) => n === name).length > 1
            )
          );
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Query names are not unique for route ${routes}: ${Array.from(duplicates)}`,
            path: ["queries"],
          });
        }
      });
    }),
  max_page: z.number().min(1).max(20).optional(),
  pages: z.number().array().min(1).optional().nullable(),
});

// ExtractionConfig schema

export const CustomSchemaFieldBaseSchema = z.object({
  name: z.string(),
  parsing_type: z.enum([
    ParsingType.Date,
    ParsingType.Float,
    ParsingType.Int,
    ParsingType.String,
    ParsingType.Boolean,
  ]),
});

export const CustomSchemaFieldSchema = CustomSchemaFieldBaseSchema.extend({
  parsing_type: z.enum([
    ParsingType.Date,
    ParsingType.Float,
    ParsingType.Int,
    ParsingType.String,
    ParsingType.Boolean,
    ParsingType.Enum,
  ]),
  enum_values: z.string().array().min(2).optional().nullable(),
}).refine(
  (custom_field) =>
    custom_field.parsing_type === ParsingType.Enum
      ? !!custom_field.enum_values
      : !custom_field.enum_values,
  {
    message: "Enum values must be provided if and only if parsing type is Enum",
  }
);

const ExtractionQueryBaseBaseSchema = QuerySchema.extend({
  name: z.string(),
  parsing_type: z.nativeEnum(ParsingType).optional().nullable(),
  type: z.nativeEnum(ExtractionType),
  custom_schema: CustomSchemaFieldBaseSchema.array()
    .optional()
    .nullable()
    .refine(
      (custom_schema) =>
        !custom_schema ||
        new Set(custom_schema.map(({ name }) => name)).size ===
          custom_schema.length,
      { message: "Custom schema field name must be unique" }
    ),
});

const ExtractionQueryBaseSchema = ExtractionQueryBaseBaseSchema.extend({
  parsing_type: z.nativeEnum(ParsingType),
  enum_values: z.string().array().min(2).optional().nullable(),
  custom_schema: CustomSchemaFieldSchema.array()
    .optional()
    .nullable()
    .refine(
      (custom_schema) =>
        !custom_schema ||
        new Set(custom_schema.map(({ name }) => name)).size ===
          custom_schema.length,
      { message: "Custom schema field name must be unique" }
    ),
  expected_value: z.any().optional().nullable(),
  many: z.boolean().optional(),
});

const LLMRoutedExtractionParametersSchema = z.object({
  context_description: z.string().optional().nullable(),
});

const LLMConfigExtractionParametersSchema =
  LLMRoutedExtractionParametersSchema.extend({
    chunk_size: z.number().min(1).max(20).optional(),
  });

const LLMExtractionQuerySchema = ExtractionQueryBaseSchema.extend({
  query: z.string(),
  type: z.literal(ExtractionType.LLM),
});

const RegexExtractionParametersSchema = z.object({
  match: z.nativeEnum(RegexMatch).optional(),
  group_number: z.number().optional().nullable(),
});

const BaseRegexExtractionQuerySchema = ExtractionQueryBaseSchema.extend({
  pattern: z.string().refine(
    (pattern) => {
      try {
        new RegExp(pattern);
        return true;
      } catch (e) {
        return false;
      }
    },
    { message: "pattern is not a valid regex pattern" }
  ),
  params: RegexExtractionParametersSchema.optional(),
  type: z.literal(ExtractionType.REGEX),
});

const RegexExtractionQuerySchema = BaseRegexExtractionQuerySchema.refine(
  (query) => {
    if (
      !query.params?.match ||
      [RegexMatch.EXISTS, RegexMatch.FIRST, RegexMatch.LAST].includes(
        query.params.match
      )
    ) {
      return !query.many;
    }
    return true;
  },
  { message: "Many should be False when match is EXISTS, FIRST or LAST" }
)
  .superRefine((query, ctx) => {
    if (query.params?.match === RegexMatch.EXISTS) {
      if (!(query.parsing_type === ParsingType.Boolean)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Parsing Type ${query.parsing_type} is not valid with match ${query.params?.match}, ${query.params}`,
        });
      }
    }
  })
  .refine(
    (query) => {
      if (query.params?.match === RegexMatch.ALL) {
        return !!query.many;
      }
      return true;
    },
    { message: "Many should be True when match is ALL" }
  );

const PredefinedQuerySchema = QuerySchema.extend({
  name: z.string(),
  query: z.nativeEnum(PredefinedExtraction),
  type: z.literal(ExtractionType.PREDEFINED),
  expected_value: z.any().optional().nullable(),
});

const ComputedQuerySchema = ExtractionQueryBaseBaseSchema.extend({
  type: z.literal(ExtractionType.COMPUTED),
  expression: z.string(),
});

const AggregatedQuerySchema = ExtractionQueryBaseBaseSchema.extend({
  type: z.literal(ExtractionType.AGGREGATE),
  expression: z.string(),
});

export const ExtractionQuerySchema = z
  .union([
    LLMExtractionQuerySchema,
    RegexExtractionQuerySchema,
    PredefinedQuerySchema,
    ComputedQuerySchema,
    AggregatedQuerySchema,
  ])
  .pipe(
    z.discriminatedUnion("type", [
      LLMExtractionQuerySchema,
      BaseRegexExtractionQuerySchema,
      PredefinedQuerySchema,
      ComputedQuerySchema,
      AggregatedQuerySchema,
    ])
  );

export const ExtractionRoutedQueriesSchema = z.object({
  queries: ExtractionQuerySchema.array().min(1),
  routes: z.string().array().min(1),
  params: LLMRoutedExtractionParametersSchema.optional().nullable(),
});

const ExtractionConfigSchema = z.object({
  queries: z
    .union([ExtractionRoutedQueriesSchema, ExtractionQuerySchema])
    .array()
    .min(1)
    .superRefine((queries, ctx) => {
      const queryList: string[] = [];
      const queryDict = new Map<string, string[]>();

      // Process all queries
      queries.forEach((query) => {
        if (!("queries" in query)) {
          queryList.push(query.name);
        } else {
          const routeKey = String(query.routes);
          if (!queryDict.has(routeKey)) {
            queryDict.set(routeKey, []);
          }
          query.queries.forEach((q) => {
            queryDict.get(routeKey)?.push(q.name);
          });
        }
      });

      // Check query name uniqueness in the main list
      const uniqueQueryNames = new Set(queryList);
      if (uniqueQueryNames.size !== queryList.length) {
        const duplicates = new Set(
          queryList.filter(
            (name) => queryList.filter((n) => n === name).length > 1
          )
        );
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Query names are not unique: ${Array.from(duplicates)}`,
          path: ["queries"],
        });
      }

      // Check query name uniqueness for each route
      queryDict.forEach((queryNames, routes) => {
        queryNames.push(...queryList);
        const uniqueRouteQueryNames = new Set(queryNames);
        if (uniqueRouteQueryNames.size !== queryNames.length) {
          const duplicates = new Set(
            queryNames.filter(
              (name) => queryNames.filter((n) => n === name).length > 1
            )
          );
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Query names are not unique for route ${routes}: ${Array.from(duplicates)}`,
            path: ["queries"],
          });
        }
      });
    }),
  params: LLMConfigExtractionParametersSchema.optional(),
});

// Validation Config
const ValidationRuleBaseSchema = z.object({
  name: z.string(),
  severity: z.nativeEnum(ValidationSeverity).optional(),
  success_message: z.string().optional(),
  error_message: z.string().optional(),
  untreatable_message: z.string().optional(),
});

const ValidationRuleSchema = ValidationRuleBaseSchema.extend({
  expression: z.string(),
});

const LLMValidationRuleSchema = ValidationRuleBaseSchema.extend({
  check: z.string(),
});

export const ValidationConfigSchema = z
  .object({
    document_rules: ValidationRuleSchema.array()
      .nullable()
      .optional()
      .refine(
        (rules) => {
          return rules
            ? rules.length === new Set(rules.map((rule) => rule.name))?.size
            : true;
        },
        { message: "Validation rule names are not unique" }
      ),
    asset_rules: z
      .union([ValidationRuleSchema, LLMValidationRuleSchema])
      .array()
      .nullable()
      .optional()
      .refine(
        (rules) => {
          return rules
            ? rules.length === new Set(rules.map((rule) => rule.name))?.size
            : true;
        },
        { message: "Validation rule names are not unique" }
      ),
    inputs: z.record(z.string(), z.any()).optional(),
  })
  .refine(
    (validation) => {
      return (
        [
          ...(validation.asset_rules ?? []),
          ...(validation.document_rules ?? []),
        ].length > 0
      );
    },
    { message: "Validation should have at least one rule" }
  );

const WorkflowStateSchema = z
  .object({
    name: z.string(),
    is_default: z.boolean().optional(),
    set_on_validation_success: z.boolean().optional(),
    set_on_validation_failure: z.boolean().optional(),
    set_on_validation_error: z.boolean().optional(),
  })
  .refine(
    (status) => {
      return (
        [
          status.is_default,
          status.set_on_validation_success,
          status.set_on_validation_failure,
          status.set_on_validation_error,
        ].filter(Boolean).length <= 1
      );
    },
    {
      message:
        "Each status can only have one condition set (default, success, failure, or error)",
    }
  );

// PipelineConfig schema
export const PipelineConfigSchema = z
  .object({
    language: z.nativeEnum(Language),
    split: SplitConfigSchema.nullable().optional(),
    classification: ClassificationConfigSchema.nullable().optional(),
    extraction: ExtractionConfigSchema.nullable().optional(),
    validation: ValidationConfigSchema.nullable().optional(),
    workflow: WorkflowStateSchema.array().min(1).optional().nullable(),
    synthesis: z.string().nullable().optional(),
  })
  .refine(
    (config) =>
      !!config.split || !!config.classification || !!config.extraction,
    {
      message:
        "At least one of split, classification and extraction should be defined",
    }
  )
  .refine(
    (config) => {
      return (
        !config.workflow ||
        new Set(config.workflow.map((s) => s.name)).size ===
          config.workflow.length
      );
    },
    { message: "Status names must be unique", path: ["workflow"] }
  )
  .refine(
    (config) => {
      const booleanFields = [
        "is_default",
        "set_on_validation_success",
        "set_on_validation_failure",
        "set_on_validation_error",
      ] as const;

      return booleanFields.every(
        (field) =>
          !config.workflow ||
          config.workflow.filter((status) => status[field]).length <= 1
      );
    },
    {
      message:
        "Each condition (default, success, failure, error) can only be set for one status",
      path: ["workflow"],
    }
  )
  .superRefine((config, ctx) => {
    let routes =
      config.split?.segmentation.classes?.map(
        (classe) => `split.${classe.name}`
      ) || [];
    config.split?.segmentation.default &&
      routes.push(config.split?.segmentation.default.name);

    if (config.classification) {
      config.classification.queries.forEach(
        (query) =>
          "queries" in query &&
          query.routes.forEach(
            (route) =>
              !routes.includes(route) &&
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Invalid route ${route} in classification`,
              })
          )
      );
      config.classification.queries.forEach((query) => {
        if ("queries" in query) {
          query.queries.forEach((q) => {
            q.classes.forEach((classe) =>
              routes.push(`classification.${q.name}.${classe.name}`)
            );
            q.default &&
              routes.push(`classification.${q.name}.${q.default.name}`);
          });
        } else {
          query.classes.forEach((classe) =>
            routes.push(`classification.${query.name}.${classe.name}`)
          );
          query.default &&
            routes.push(`classification.${query.name}.${query.default.name}`);
        }
      });
    }

    if (config.extraction) {
      return config.extraction.queries.forEach(
        (query) =>
          "queries" in query &&
          query.routes.forEach(
            (route) =>
              !routes.includes(route) &&
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Invalid route ${route} in extraction`,
              })
          )
      );
    }
  });

export const PipelineConfigFormSchema = z.object({
  config: PipelineConfigSchema,
  name: z.string().min(1),
});

export type ClassificationQuerySchemaType = z.infer<
  typeof ClassificationQuerySchema
>;
export type ClassificationRoutedQuerySchemaType = z.infer<
  typeof ClassificationRoutedQueriesSchema
>;
export type ExtractionQuerySchemaType = z.infer<typeof ExtractionQuerySchema>;
export type ExtractionRoutedQuerySchemaType = z.infer<
  typeof ExtractionRoutedQueriesSchema
>;
export type PipelineConfigSchemaType = z.infer<typeof PipelineConfigSchema>;
export type PipelineConfigForm = z.infer<typeof PipelineConfigFormSchema>;
